import {Component, effect, inject, Predicate, signal} from '@angular/core';
import {NgClass} from "@angular/common";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {AssetComponent} from "src/app/presentation/common/asset.component";
import {AnimalSvgComponent} from "src/app/presentation/common/icons/animal-svg/animal-svg.component";
import {ToCapitalizedTextPipe} from "src/app/presentation/common/pipes/ToCapitalizedTextPipe";
import {PaddockComponent} from "src/app/presentation/main-view/information-center/animals/paddock/paddock.component";
import {OopSvgComponent} from "src/app/presentation/common/icons/oop-svg/oop-svg.component";
import {StoppedMovingSvgComponent} from "src/app/presentation/common/icons/stopped-moving-svg/stopped-moving-svg.component";
import {AbnormalActivitySvgComponent} from "src/app/presentation/common/icons/abnormal-activity-svg/abnormal-activity-svg.component";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {environment} from "src/app/environments/environment";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {allAnimalTypes, Animal, AnimalType, Paddock} from "src/app/infrastructure/moovement-api/models/asset";
import {NetworkStatusStore} from 'src/app/infrastructure/network/network-status.store';
import { fourHours, sevenDays, thirtyDays, threeDays, twentyFourHours } from 'src/app/presentation/common/globals';
import { AggregatedAssetStateStore } from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import { unassignedAnimalsPaddock } from 'src/app/domain/assets/paddocks/unassigned-animals-paddock';
import { PaddockStore } from 'src/app/domain/assets/paddocks/paddock.store';
import { AnimalsStore } from 'src/app/domain/assets/animals/animals.store';
import { AnimalSelectionStore } from 'src/app/domain/assets/animals/animal-selection.store';
import { UserStore } from 'src/app/domain/user/user.store';

enum LastSeenPeriod {
  LAST_4_HOURS = 'Last 4 hours',
  LAST_24_HOURS = 'Last 24 hours',
  LAST_3_DAYS = 'Last 3 days',
  LAST_7_DAYS = 'Last 7 days',
  LAST_30_DAYS = 'Last 30 days',
}

enum SortOption {
  NAME_ASCENDING = 'Name (a-z)',
  NAME_DESCENDING = 'Name (z-a)',
  LAST_SEEN_ASCENDING = 'Time last seen (oldest first)',
  LAST_SEEN_DESCENDING = 'Time last seen (newest first)',
}

const defaultFilter = (_: Animal) => true;
const defaultSortFunction: (a: Animal, b: Animal) => number = (a, b) => 0;

const openPaddocks = new Set<string>();

interface PreviousFilters {
  alertStatuses: Samples.AlertStatus[];
  animalTypes: AnimalType[];
  lastSeenPeriod?: LastSeenPeriod;
  sort?: SortOption;
}

const previousFilters: PreviousFilters = {
  alertStatuses: [],
  animalTypes: []
}

@Component({
  selector: 'app-animals',
  imports: [
    NgClass,
    AnimalSvgComponent,
    ToCapitalizedTextPipe,
    PaddockComponent,
    OopSvgComponent,
    StoppedMovingSvgComponent,
    AbnormalActivitySvgComponent,
    GenericButtonComponent,
    SpinnerComponent
  ],
  templateUrl: './animals.component.html',
  styleUrl: './animals.component.css'
})
export class AnimalsComponent extends AssetComponent {

  openPaddocks = openPaddocks;

  selectedAlertStatuses: Set<Samples.AlertStatus> = new Set()
  selectedAnimalTypes: AnimalType[] = []
  unknown: number = 0
  cows: number = 0
  bulls: number = 0
  calves: number = 0
  steers: number = 0
  numberOfAnimalsOutOfBounds = 0;
  numberOfAnimalsThatStoppedMoving = 0;
  numberOfAnimalsWithLowActivity = 0;
  numberOfAnimalsWithHighActivity = 0;
  unassignedAnimals = signal<Animal[]>([]);
  allAnimals = signal<Animal[]>([]);
  filteredAnimals = signal<Animal[]>([]);
  allPaddocks: Array<Paddock> = []
  filteredPaddocks: Array<Paddock> = []
  filtersOpen: boolean = false
  animalsCanBeAltered: boolean = false
  animalsCanBeDeleted: boolean = false
  allPaddocksOpen: boolean = true
  allPaddocksSelected: boolean = false

  private animalTypeFilter = signal<Predicate<Animal>>(defaultFilter);
  private alertStatusFilter = signal<Predicate<Animal>>(defaultFilter);
  private lastSeenFilter = signal<Predicate<Animal>>(defaultFilter);
  private paddockFilter = signal<Predicate<Animal>>(defaultFilter);

  selectedLastSeenPeriod?: LastSeenPeriod;
  selectedSort?: SortOption;
  selectedSortMessage?: string;
  private sortFunction = signal(defaultSortFunction);
  Array = Array;
  AnimalType = AnimalType;
  allAnimalTypes = allAnimalTypes;
  AlertStatus = Samples.AlertStatus;
  LastSeenPeriod = LastSeenPeriod;
  SortOption = SortOption;
  environment = environment;

  networkStatusStore = inject(NetworkStatusStore);
  paddockStore = inject(PaddockStore);
  userStore = inject(UserStore);

  private lastSeenByBLEScannerPerAnimal: Map<string, number> = new Map();
  private lastSeenByGatewayPerAnimal: Map<string, number> = new Map();
  private lastSeenBySatellitePerAnimal: Map<string, number> = new Map();

  private aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private animalSelectionStore = inject(AnimalSelectionStore);
  private animalsStore = inject(AnimalsStore);

  constructor(dataSynchronisationService: DataSynchronisationService, dataSubscriptionService: DataSubscriptionService, viewportService: ViewportService) {
    super(dataSynchronisationService, dataSubscriptionService, viewportService);

    effect(() => {
      this.animalsCanBeAltered = this.filteredAnimals().some(animal => this.userStore.canEditPersistable(animal));
      this.animalsCanBeDeleted = this.filteredAnimals().some(animal => this.userStore.canDeletePersistable(animal));
    });

    effect(() => {
      const states = this.aggregatedAssetStateStore.entities();
      if (!states) return;

      let numberOfAnimalsOutOfBounds = 0;
      let numberOfAnimalsThatStoppedMoving = 0;
      let numberOfAnimalsWithLowActivity = 0;
      let numberOfAnimalsWithHighActivity = 0;

      for (let state of states) {
        const lastSeenByBLEScannerAt = state.lastSeenByBLEScannerAt;
        if (lastSeenByBLEScannerAt) this.lastSeenByBLEScannerPerAnimal.set(state.id, lastSeenByBLEScannerAt.getTime());

        const lastSeenByGatewayAt = state.lastSeenByGatewayAt;
        if (lastSeenByGatewayAt) this.lastSeenByGatewayPerAnimal.set(state.id, lastSeenByGatewayAt.getTime());

        const lastSeenBySatelliteAt = state.lastGPSFixAt;
        if (lastSeenBySatelliteAt) this.lastSeenBySatellitePerAnimal.set(state.id, lastSeenBySatelliteAt.getTime());

        if (state.outOfFarmSince || state.outOfPaddockSince) ++numberOfAnimalsOutOfBounds;

        if (state.stoppedMovingSince) ++numberOfAnimalsThatStoppedMoving;

        if (state.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_LOW_ACTIVITY) ++numberOfAnimalsWithLowActivity;
        if (state.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_HIGH_ACTIVITY) ++numberOfAnimalsWithHighActivity;
      }

      this.numberOfAnimalsOutOfBounds = numberOfAnimalsOutOfBounds;
      this.numberOfAnimalsThatStoppedMoving = numberOfAnimalsThatStoppedMoving;
      this.numberOfAnimalsWithLowActivity = numberOfAnimalsWithLowActivity;
      this.numberOfAnimalsWithHighActivity = numberOfAnimalsWithHighActivity;
    });

    effect(() => this.filterAnimals());

    effect(() => this.filterPaddocks());

    effect(() => {
      const selectedPaddock = this.paddockStore.selectedPaddock();
      this.filterByPaddock(selectedPaddock);
    });

    effect(() => {
      if (this.paddockStore.loading()) return;

      const paddocks = this.paddockStore.entities();
      this.allPaddocks = [...paddocks];

      const unassignedAnimals = this.unassignedAnimals();
      if (unassignedAnimals.length > 0) this.allPaddocks.push(unassignedAnimalsPaddock);

      this.filteredPaddocks = this.allPaddocks;

      this.allPaddocks.sort((pa, pb) => {
        return this.getFilteredAnimalsFor(pb).length - this.getFilteredAnimalsFor(pa).length;
      });
    });

    effect(() => {
      if (this.animalsStore.loading()) return;

      const animals = this.animalsStore.entities();
      this.allAnimals.set(animals);
      this.filteredAnimals.set(animals);

      const unassignedAnimals = this.animalsStore.unassignedAnimals();
      this.unassignedAnimals.set(unassignedAnimals);

      this.divideAnimalBadges();
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.animalSelectionStore.reset();

    if (previousFilters.alertStatuses) this.filterByAlertStatus(previousFilters.alertStatuses);
    if (previousFilters.animalTypes) this.filterByAnimalType(previousFilters.animalTypes);
    if (previousFilters.lastSeenPeriod) this.filterByLastSeenPeriod(previousFilters.lastSeenPeriod);
    if (previousFilters.sort) this.sortBy(previousFilters.sort);
  }

  filterResults(input: string) {
    this.reset()

    const value = input.toLowerCase()

    this.filteredAnimals.set(this.allAnimals().filter(
      animal => {
        return animal?.visualTag?.toLowerCase().includes(value)
          || animal?.name?.toLowerCase().includes(value)
          || animal?.type?.toLowerCase().includes(value)
          || animal?.yearOfBirth?.toString()?.toLowerCase().includes(value)
      }
    ));
    this.filteredPaddocks = this.allPaddocks.filter(paddock => this.filteredAnimals().some(animal => animal.assignedPaddockId == paddock.id || (animal.assignedPaddockId == undefined && paddock === unassignedAnimalsPaddock)))
  }

  getFilteredAnimalsFor(paddock: Paddock): Array<Animal> {
    return this.filteredAnimals().filter(animal => (animal.assignedPaddockId == paddock.id) || (animal.assignedPaddockId == undefined && paddock === unassignedAnimalsPaddock))
  }

  checkUncheckAllPaddocks(): void {
    if (this.networkStatusStore.isOnline() || this.animalsCanBeAltered) {
      this.allPaddocksSelected = !this.allPaddocksSelected

      if (this.allPaddocksSelected) {
        this.animalSelectionStore.setAllEntities(this.filteredAnimals());
      } else {
        this.animalSelectionStore.reset();
      }
    }
  }

  openEditForm() {
    if (this.networkStatusStore.isOnline() || this.animalsCanBeAltered) {
      this.router.navigate(['/main/edit-animal']);
    }
  }

  sortBy(sortOption?: SortOption) {
    this.selectedSort = sortOption;
    previousFilters.sort = sortOption;

    if (sortOption) {
      this.selectedSortMessage = "Sorted by " + sortOption;
    } else {
      this.selectedSortMessage = undefined;
    }

    switch (sortOption) {
      case SortOption.NAME_ASCENDING:
        this.sortFunction.set((a, b) => this.compareByVisualTag(a, b));
        break;
      case SortOption.NAME_DESCENDING:
        this.sortFunction.set((a, b) => this.compareByVisualTag(b, a));
        break;
      case SortOption.LAST_SEEN_ASCENDING:
        this.sortFunction.set((a, b) => this.compareByLastSeen(a, b));
        break;
      case SortOption.LAST_SEEN_DESCENDING:
        this.sortFunction.set((a, b) => this.compareByLastSeen(b, a));
        break;
      default:
        this.sortFunction.set(defaultSortFunction);
        break;
    }
  }

  private compareByVisualTag(a: Animal, b: Animal) {
    return (a.visualTag || "").localeCompare(b.visualTag!);
  }

  private compareByLastSeen(a: Animal, b: Animal) {
    const lastSeenA = this.lastSeen(a);
    const lastSeenB = this.lastSeen(b);
    if (!lastSeenA && !lastSeenB) return 0;
    if (!lastSeenA) return -1;
    if (!lastSeenB) return 1;
    return lastSeenA - lastSeenB;
  }

  filterByAnimalType(animalTypes: AnimalType[], reset: boolean = false) {
    if (reset) this.reset()

    this.selectedAnimalTypes = animalTypes;
    previousFilters.animalTypes = this.selectedAnimalTypes;

    if (this.selectedAnimalTypes.length > 0) {
      this.animalTypeFilter.set(animal =>  this.selectedAnimalTypes.indexOf(animal?.type as AnimalType) !== -1);
    } else {
      this.animalTypeFilter.set(defaultFilter);
    }
  }

  filterByAlertStatus(alertStatuses: Samples.AlertStatus[], reset: boolean = false) {
    if (reset) this.reset();

    this.selectedAlertStatuses = new Set(alertStatuses);
    previousFilters.alertStatuses = alertStatuses;

    if (this.selectedAlertStatuses.size > 0) {
      this.alertStatusFilter.set(animal => this.hasAlertStatus(animal));
    } else {
      this.alertStatusFilter.set(defaultFilter);
    }
  }

  private hasAlertStatus(animal: Animal): boolean {
    const aggregatedState = this.aggregatedAssetStateStore.entityMap()[animal.id];
    if (!aggregatedState) return false;

    if (this.selectedAlertStatuses.has(Samples.AlertStatus.OUT_OF_FARM) && aggregatedState.outOfFarmSince !== undefined)
      return true;

    if (this.selectedAlertStatuses.has(Samples.AlertStatus.OUT_OF_PADDOCK) && aggregatedState.outOfPaddockSince !== undefined)
      return true;

    if (this.selectedAlertStatuses.has(Samples.AlertStatus.STOPPED_MOVING) && aggregatedState.stoppedMovingSince !== undefined)
      return true;

    if (this.selectedAlertStatuses.has(Samples.AlertStatus.ABNORMALLY_LOW_ACTIVITY) && aggregatedState.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_LOW_ACTIVITY)
      return true;

    if (this.selectedAlertStatuses.has(Samples.AlertStatus.ABNORMALLY_HIGH_ACTIVITY) && aggregatedState.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_HIGH_ACTIVITY)
      return true;

    return false;
  }

  filterByLastSeenPeriod(period?: LastSeenPeriod) {
    this.selectedLastSeenPeriod = period;
    previousFilters.lastSeenPeriod = period;

    if (this.selectedLastSeenPeriod) {
      this.lastSeenFilter.set(animal => {
        if (!period) return true;
        const lastSeen = this.lastSeen(animal);

        if (!lastSeen) return false;

        return lastSeen >= this.thresholdForPeriod(period);
      });
    } else {
      this.lastSeenFilter.set(defaultFilter);
    }
  }

  private lastSeen(animal: Animal) {
    const id = animal.id;
    const lastSeenByGateway = this.lastSeenByGatewayPerAnimal.get(id);
    const lastSeenByBLEScanner = this.lastSeenByBLEScannerPerAnimal.get(id);
    const lastSeenBySatellite = this.lastSeenBySatellitePerAnimal.get(id);

    const lastSeens = [lastSeenByGateway, lastSeenByBLEScanner, lastSeenBySatellite]
      .filter(date => date !== undefined);

    if (lastSeens.length === 0) return undefined;

    return lastSeens.reduce((previous, current) => Math.max(previous, current))
  }

  private thresholdForPeriod(period: LastSeenPeriod): number {
    const now = new Date().getTime();
    switch (period) {
      case LastSeenPeriod.LAST_4_HOURS:
        return now - fourHours;
      case LastSeenPeriod.LAST_24_HOURS:
        return now - twentyFourHours;
      case LastSeenPeriod.LAST_3_DAYS:
        return now - threeDays;
      case LastSeenPeriod.LAST_7_DAYS:
        return now - sevenDays;
      case LastSeenPeriod.LAST_30_DAYS:
        return now - thirtyDays;
      default:
        return 0;
    }
  }

  selectPaddock(paddock?: Paddock) {
    this.paddockStore.setSelectedPaddock(paddock);
  }

  filterByPaddock(paddock?: Paddock) {
    if (paddock) {
      this.paddockFilter.set(animal => {
        if (paddock === unassignedAnimalsPaddock) {
          return animal.assignedPaddockId === undefined;
        }
        return animal?.assignedPaddockId === paddock.id;
      });
    } else {
      this.paddockFilter.set(defaultFilter);
    }
  }

  reset() {
    this.filterByAlertStatus([]);
    this.filterByAnimalType([]);
    this.filterByLastSeenPeriod();
    this.filterByPaddock();
    this.allPaddocksOpen = true
    this.sortFunction.set(defaultSortFunction);
  }

  openAddAnimalComponent() {
    this.router.navigate(['/main/add-animal']);
  }

  openCloseFilters() {
    this.filtersOpen = !this.filtersOpen
  }

  getInput(target: any) {
    return target?.value as string
  }

  deleteAnimals() {
    this.router.navigate(['/main/delete-animals']);
  }

  hasAlterableAnimalSelected() {
    const selectedAnimals = this.animalSelectionStore.entities();
    const editableAnimals = selectedAnimals.filter(animal => this.userStore.canEditPersistable(animal));

    return editableAnimals.length > 0
  }

  hasDeletableAnimalsSelected() {
    const selectedAnimals = this.animalSelectionStore.entities();
    const deletableAnimals = selectedAnimals.filter(animal => this.userStore.canDeletePersistable(animal));

    return deletableAnimals.length > 0;
  }

  private divideAnimalBadges() {
    this.resetBadges()
    this.allAnimals().forEach(animal => {
      switch (animal.type) {
        case AnimalType.HEIFER:
        case AnimalType.COW:
          this.cows++;
          break;
        case AnimalType.BULL:
          this.bulls++;
          break;
        case AnimalType.BULL_CALF:
        case AnimalType.STEER_WEANER:
        case AnimalType.HEIFER_WEANER:
        case AnimalType.HEIFER_CALF:
        case AnimalType.STEER_CALF:
          this.calves++;
          break;
        case AnimalType.STEER:
          this.steers++;
          break;
        default:
          this.unknown++
      }
    })
    this.loading = false
  }

  private filterAnimals() {
    const allAnimals = this.allAnimals();
    const sortFunction = this.sortFunction();
    this.filteredAnimals.set(allAnimals.filter(animal =>
      this.alertStatusFilter()(animal) &&
      this.animalTypeFilter()(animal) &&
      this.lastSeenFilter()(animal) &&
      this.paddockFilter()(animal)
    ).sort(sortFunction));
  }

  private filterPaddocks() {
    const selectedPaddock = this.paddockStore.selectedPaddock();
    if (selectedPaddock) {
      this.filteredPaddocks = [selectedPaddock];
    } else {
      this.filteredPaddocks = [...this.allPaddocks];
    }
  }

  private resetBadges() {
    this.unknown = 0
    this.cows = 0
    this.bulls = 0
    this.steers = 0
    this.calves = 0
  }
}
